import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight, Briefcase, Camera, Github, Linkedin, Mail, MapPin, Calendar, Construction } from 'lucide-react';

const Landing = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-slate-900/80 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Çankaya</h1>
          <nav className="flex gap-6 items-center">
            <a href="https://ogulcan.cankaya.xyz" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white transition-colors">
              Portfolio
            </a>
            <a href="#contact" className="text-slate-300 hover:text-white transition-colors">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium border border-blue-500/30">
                Welcome to Çankaya.xyz
              </span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Ahmet Oğulcan
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Çankaya
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-4 max-w-3xl mx-auto">
              DevOps Engineer & Backend Developer
            </p>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-12">
              Building scalable infrastructure by day, capturing life's moments by heart.
            </p>

            {/* Main CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="https://ogulcan.cankaya.xyz" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg group transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/50"
                >
                  <Briefcase className="mr-2 h-5 w-5" />
                  View My Portfolio
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a 
                href="https://elogi.cankaya.xyz" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto border-2 border-slate-600 hover:border-slate-500 bg-slate-800/50 hover:bg-slate-700/50 text-white px-8 py-6 text-lg group transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  <Camera className="mr-2 h-5 w-5 text-cyan-400" />
                  Our Photo Gallery
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 mt-20">
            <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
                  <Briefcase className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Professional Work</h3>
                  <p className="text-slate-400 mb-4">
                    Explore my journey in DevOps and Backend Development. Over 5 years of experience automating, optimizing, and securing cloud deployments with AWS, Kubernetes, and modern DevOps practices.
                  </p>
                  <a 
                    href="https://ogulcan.cankaya.xyz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-medium inline-flex items-center group"
                  >
                    Explore Portfolio
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-cyan-500/20 rounded-lg border border-cyan-500/30">
                  <Camera className="h-6 w-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Personal Life</h3>
                  <p className="text-slate-400 mb-4">
                    Beyond the code and infrastructure, discover the personal moments and memories I gather. A collection of our adventures, celebrations, and everyday joys.
                  </p>
                  <a 
                    href="https://elogi.cankaya.xyz" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-400 hover:text-cyan-300 font-medium inline-flex items-center group"
                  >
                    View Gallery
                    <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:shadow-xl hover:shadow-orange-500/10 transition-all duration-300 hover:-translate-y-1">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-500/20 rounded-lg border border-orange-500/30">
                  <Calendar className="h-6 w-6 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Stories & Events</h3>
                  <p className="text-slate-400 mb-4">
                    Technical events, conferences, and experiences from the DevOps and cloud infrastructure world. Insights, learnings, and memorable moments from the tech community.
                  </p>
                  <a 
                    href="/events" 
                    className="text-orange-400 hover:text-orange-300 font-medium inline-flex items-center group"
                  >
                    Coming Soon
                    <Construction className="ml-1 h-4 w-4" />
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 bg-slate-900/50 border-y border-slate-800">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">About Me</h2>
            <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
          </div>
          <div className="prose prose-lg mx-auto text-slate-300">
            <p className="text-center text-lg leading-relaxed">
              Currently working as a System DevOps Engineer at Nexent Bank N.V. in Bucharest, Romania, 
              I specialize in handling migrations, managing OCP and AAP pipelines, and creating comprehensive 
              logging and monitoring systems. When I'm not optimizing cloud infrastructure, I enjoy capturing 
              life's precious moments with my family and friends.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-slate-950">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Get In Touch</h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-12"></div>
          
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <a 
              href="mailto:ahmetogulcancankaya@gmail.com" 
              className="flex items-center gap-2 text-slate-400 hover:text-blue-400 transition-colors"
            >
              <Mail className="h-5 w-5" />
              <span>ahmetogulcancankaya@gmail.com</span>
            </a>
            <div className="flex items-center gap-2 text-slate-400">
              <MapPin className="h-5 w-5" />
              <span>Bucharest, Romania</span>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <a 
              href="https://github.com/AhmetOgulcanCankaya" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors border border-slate-700 hover:border-slate-600"
            >
              <Github className="h-6 w-6 text-slate-300" />
            </a>
            <a 
              href="https://www.linkedin.com/in/ahmet-ogulcan-cankaya/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors border border-slate-700 hover:border-slate-600"
            >
              <Linkedin className="h-6 w-6 text-slate-300" />
            </a>
            <a 
              href="mailto:ahmetogulcancankaya@gmail.com"
              className="p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors border border-slate-700 hover:border-slate-600"
            >
              <Mail className="h-6 w-6 text-slate-300" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-slate-950 text-slate-500 border-t border-slate-800">
        <div className="container mx-auto text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} Ahmet Oğulcan Çankaya. All rights reserved.
          </p>
          <p className="text-xs mt-2">
            Built with care for family, friends, and the future.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;